import {GROUND_BACKGROUNDS} from '../groundBackgrounds';
import {ASHFALL_EFFECT} from './ashfall';

/**
 * ДЫМ НАД КРАТЕРОМ — эффект вулканической подложки (volcano_1).
 *
 * На картинке — действующий кратер у правого верхнего угла поля: красное жерло
 * и нарисованный шлейф дыма, уходящий вправо-вверх. Эффект оживляет его (см.
 * utils/effects/biome/craterPlume):
 *  - по нарисованному шлейфу один за другим плывут серые клубы: быстро
 *    вырываются из жерла, разбухают и тают. Шлейф расходится от жерла конусом
 *    не уже нарисованного дыма: у жерла он узкий, к концу пути — вчетверо шире;
 *  - жерло и огоньки на кромке кратера «дышат» теплым светом — самая заметная
 *    часть эффекта;
 *  - изредка вулкан «выдыхает»: жерло разгорается, и из него разом выходит
 *    горсть клубов потемнее;
 *  - над полем идет мелкий вулканический пепел с тлеющими угольками: кратер
 *    лежит за углом поля и в кадр попадает не всегда, а пепел виден там, куда
 *    смотрит игрок.
 *
 * ШЛЕЙФ ИДЕТ ПО НАРИСОВАННОМУ НАПРАВЛЕНИЮ, А НЕ ПО ВЕТРУ МАТЧА: дым уже
 * нарисован уходящим вправо-вверх, и при ветре справа налево из одного жерла
 * вышло бы два шлейфа в разные стороны.
 *
 * Кратер лежит за полем, шлейф уходит от поля к углу картинки, поэтому отрядов
 * он не закрывает. Дым и свет лежат на земле (под затемнением края картинки):
 * угол пейзажа гасит шлейф так же, как нарисованный дым.
 *
 * Размеры — в долях стороны картинки пейзажа: кратер растет и уменьшается
 * вместе с ней. Размер клуба и пятна света — размер кадра текстуры: плотная
 * часть клуба — середина кадра, половина его ширины (дальше четверти кадра от
 * центра клуб вдвое прозрачнее и быстро сходит на нет), видимое пятно света —
 * тоже вдвое меньше кадра.
 */
export const CRATER_PLUME_KIND = 'craterPlume';

// Пепел вулкана — тот же, что над пепельной равниной (utils/effects/biome/ashfall):
// размер хлопьев и ореолов угольков — ее числа как есть
const PLAIN_ASH = ASHFALL_EFFECT.backgrounds[GROUND_BACKGROUNDS.ASH_1];

export const CRATER_PLUME_EFFECT = Object.freeze({
  kind: CRATER_PLUME_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.VOLCANO_1]: Object.freeze({
      /**
       * Нарисованный шлейф: от основания дымового столба в кратере до места,
       * где дальше начинается затемнение края картинки (доли ее стороны).
       * Точки пути — середина нарисованного дыма (см. замер ниже): конус
       * клубов расходится от пути в обе стороны поровну
       */
      path: [
        {u: 0.75, v: 0.198},
        {u: 0.781, v: 0.174},
        {u: 0.823, v: 0.152},
        {u: 0.882, v: 0.118},
      ],
      /**
       * ШИРИНА НАРИСОВАННОГО ДЫМА поперек шлейфа — доли стороны картинки, на
       * долях длины пути (share). С ней сверяется конус клубов: он обязан быть
       * не уже (см. тест построителя), иначе нарисованный дым торчит по бокам
       * анимированного неподвижной каймой.
       *
       * Замер по самой картинке (4096 точек в стороне): сечение — поперек
       * прямой от начала пути к его концу, через точку пути; яркость — медиана
       * в окне 0.006 стороны (пена прибоя и огни лавы в нее не попадают). Дым —
       * ярче 62–68 (темная порода и море под ним — 30–60, плотный дым — 90–150);
       * край — где яркость уходит ниже порога, просветы уже 0.016 — еще дым.
       * Края у дыма размытые: от порога ширина меняется на десятую долю, в
       * таблице — большая. Середина дыма при этом лежит на пути (не дальше
       * 0.004 от него). У самого жерла (до пятой доли пути) столб дыма не
       * мерян: он сливается с освещенной кромкой кратера; на глаз он там
       * около 0.02. Замер ручной: тест сверяет с таблицей конус клубов, но не
       * саму таблицу с картинкой — сменится картинка, мерить заново
       */
      paintedWidths: [
        {share: 0.2, width: 0.044},
        {share: 0.4, width: 0.054},
        {share: 0.6, width: 0.076},
        {share: 0.8, width: 0.09},
        {share: 1, width: 0.11},
      ],

      // Клубы шлейфа: рождаются один за другим, каждый — своя группа
      plume: {
        firstDelayMs: {min: 200, max: 700},
        // Клуб живет около 19 с, новый — раз в 0.8 с: в шлейфе разом около 24 клубов
        intervalMs: {min: 600, max: 1000},
        maxGroups: 30,
        // Шлейф не начинается с пустого места: столько клубов рождаются уже в пути
        prewarm: 24,
        lifeMs: {min: 16000, max: 22000},
        // Путь по шлейфу: 1 - (1 - t)^power — быстро из жерла, дальше медленный дрейф
        progressPower: 1.6,
        // Размер кадра клуба: у жерла и в конце пути, доли стороны; клуб не
        // меньше и не больше стольких ширин ячейки, каким бы ни было поле.
        // Крупнее некуда: клуб выдоха (в 1.25 раза больше, и еще до 15 % —
        // разброс размера) выходит в 0.138 стороны, а потолок размера наземной
        // частицы — 0.15 (общий договор эффектов)
        sizeShare: {from: 0.024, to: 0.096},
        sizeCells: {min: 0.5, max: 4},
        // Клубы разного размера (доля от расчетного)
        sizeJitter: {min: 0.85, max: 1.15},
        // КОНУС ШЛЕЙФА: полуширина разброса клубов поперек шлейфа — у жерла и
        // в конце пути, доли стороны. Клуб уходит от жерла своим лучом: его
        // место поперек конуса (от края до края, равномерно) одно на всю
        // жизнь. Видимая ширина шлейфа — разброс и по четверти кадра клуба с
        // каждой стороны: от 0.03 у жерла до 0.14 в конце пути, на четверть
        // шире нарисованного дыма (paintedWidths) — с запасом на то, что
        // крайних клубов в конусе немного
        scatterShare: {from: 0.01, to: 0.044},
        // Клуб покачивается поперек шлейфа: на столько своих размеров
        swayShare: 0.12,
        swayMs: {min: 5000, max: 9000},
        spinDegPerSecond: 10,
        // У жерла дым темнее, дальше светлеет (доли срока, за которые меняется краска)
        tints: {young: 0x9a9aa0, old: 0xc9ccd0, turnFrom: 0.08, turnTo: 0.22},
        // Плотность: проступает за fadeInShare срока и тает как (1 - t)^fadePower.
        // Плотнее 0.4 сумеречный пейзаж превращается в белую вату
        alpha: 0.34,
        fadeInShare: 0.08,
        fadePower: 1.2,
      },

      // Свет жерла и огоньков на кромке кратера: светится поверх темной породы
      glow: {
        // Дыхание неровное: две волны с несовпадающими периодами; доля второй — mix
        breathMs: {min: 2600, max: 3400},
        secondPeriodShare: 0.37,
        secondMix: 0.3,
        // Пятна одного очага (beat) дышат в такт, разные очаги — вразнобой
        spots: [
          // Жерло: широкое рыжее пятно и светлое ядро поверх него
          {
            beat: 'vent', u: 0.739, v: 0.222, sizeShare: 0.04, tint: 0xff5a28, alpha: {min: 0.15, max: 0.4},
            sublayer: 0,
          },
          {
            beat: 'vent', u: 0.739, v: 0.222, sizeShare: 0.017, tint: 0xffb070, alpha: {min: 0.3, max: 0.65},
            sublayer: 1,
          },
          // Два огонька на кромке кратера: слабее и не в такт жерлу
          {
            beat: 'rimNear', u: 0.761, v: 0.205, sizeShare: 0.019, tint: 0xff5a28, alpha: {min: 0.1, max: 0.25},
            sublayer: 0,
          },
          {
            beat: 'rimFar', u: 0.79, v: 0.2, sizeShare: 0.019, tint: 0xff5a28, alpha: {min: 0.1, max: 0.25},
            sublayer: 0,
          },
        ],
      },

      // Выдох: жерло разгорается, из него разом выходит горсть клубов
      exhale: {
        firstDelayMs: {min: 12000, max: 24000},
        // Пауза — от начала предыдущего выдоха; клубы выдоха живут дольше паузы,
        // поэтому разом бывает два выдоха: новый и дотаивающий
        intervalMs: {min: 25000, max: 45000},
        maxGroups: 2,
        // За сколько выходят клубы выдоха: выброс вдвое гуще обычного
        burstMs: 4000,
        puffCount: {min: 6, max: 8},
        // Клубы выдоха крупнее, плотнее и темнее обычных
        sizeScale: 1.25,
        alpha: 0.4,
        youngTint: 0x7c7c84,
        // Вспышка жерла: разгорается, держится весь выброс и гаснет
        flare: {
          riseMs: 700,
          decayMs: 3000,
          // Пятно вспышки вырастает от такой доли своего размера
          startScale: 0.8,
          spots: [
            {u: 0.739, v: 0.222, sizeShare: 0.052, tint: 0xff5a28, alpha: 0.22, sublayer: 0},
            {u: 0.739, v: 0.222, sizeShare: 0.022, tint: 0xffc890, alpha: 0.3, sublayer: 1},
          ],
        },
      },

      /**
       * Пепел над полем: числа пепельной равнины, свои — только число хлопьев
       * и доли их видов.
       *
       * РАЗМЕР — КАК НАД РАВНИНОЙ, и здесь он не пересчитывается: кадр хлопка
       * 0.13–0.24 размера ячейки, вдвое мельче первых крупных хлопьев
       * (0.26–0.48) — до поля от кратера долетает мелкий пепел. Мелким он стал
       * сначала здесь (просьба владельца игры: «значительно менее крупными»),
       * потом таким же стал пепел равнины — и множитель размера отсюда ушел:
       * оставь его, пепел вулкана измельчал бы второй раз. Вместе с хлопком
       * мелок и ореол уголька (его кадр — столько же кадров хлопка, glowScale).
       *
       * ЧИСЛОМ хлопьев — вдвое меньше, чем над равниной (там пепел — весь
       * эффект, здесь — добавка к кратеру), но в два с половиной раза больше,
       * чем при крупных хлопьях: было 0.12 на ячейку и не больше 22 — горсть
       * белых пятен на весь кадр. Гуще нельзя: над отрядами разом летит не
       * больше сотни спрайтов (общий договор эффектов), а у каждого уголька их
       * два — сейчас их там до 90.
       *
       * ЯРКИХ И СЕРО-ЧЕРНЫХ ХЛОПЬЕВ — ПРИМЕРНО ПОРОВНУ (просьба владельца
       * игры): половина — тлеющие угольки, вулкан рядом; вторая половина —
       * светлый пепел и угольно-темный. Светлого в ней больше: равнина у
       * вулкана сама темная, темный хлопок на ней виден хуже светлого, да и
       * остывший уголек — тоже темный хлопок
       */
      ashfall: Object.freeze({
        ...PLAIN_ASH,
        densityPerCell: 0.3,
        maxFlakes: 55,
        pale: {...PLAIN_ASH.pale, share: 0.3},
        charcoal: {...PLAIN_ASH.charcoal, share: 0.2},
        cinder: {...PLAIN_ASH.cinder, share: 0.5},
      }),
    }),
  }),
});
